import { SkyDialog, SkyMessageBar, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { InstanceAdminView } from "@shared/types/instancesAdmin";
import { useStorageRemovalCheck } from "@/hooks/use-instances-admin";
import { formatCalendarDay } from "@/lib/time/displayTime";
import { ErrorState } from "./ScreenState";

/** The sentence the owner asked for (2026-10-09) when the chain was never exported. */
export const NEVER_ARCHIVED =
  "This instance's audit history was never archived. It cannot be recovered.";

/**
 * Mark a disabled instance's storage removed (platform 0033): its database or schema is already
 * gone, outside the product. The server checks that at the moment of marking and refuses while
 * the storage exists. Disabling never removes storage; this mark records that something else did.
 *
 * Before anyone marks, the dialog reads the preflight (`GET .../storage-removal-check`) and says
 * whether the write-once archive holds the instance's audit history. The mark is allowed either
 * way (the history is not coming back by refusing it); the dialog only makes the loss plain.
 */
export function MarkStorageRemovedDialog({
  open,
  onOpenChange,
  instance,
  onConfirm,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: InstanceAdminView | null;
  onConfirm: (reason: string) => void;
  isSaving: boolean;
  error: unknown;
}) {
  // Empty on every opening: the caller remounts it with a key.
  const [reason, setReason] = useState("");
  const [asked, setAsked] = useState(false);
  const check = useStorageRemovalCheck(open && instance ? instance.id : null);

  if (!instance) return null;
  const trimmed = reason.trim();

  function archiveMessage() {
    if (check.isPending) {
      return (
        <SkyMessageBar
          intent="info"
          message="Checking whether its audit history was archived."
          testId="storage-removed-archive-checking"
        />
      );
    }
    if (check.isError) {
      return (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          message="Whether its audit history was archived could not be checked. The audit event will record it."
          testId="storage-removed-archive-unknown"
        />
      );
    }
    if (check.data.archive === "none") {
      return (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="No archive"
          message={NEVER_ARCHIVED}
          testId="storage-removed-archive-none"
        />
      );
    }
    const rows = check.data.rows;
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        message={`The write-once archive keeps ${rows.toLocaleString("en-US")} ${
          rows === 1 ? "row" : "rows"
        } of its audit history${
          check.data.through ? `, through ${formatCalendarDay(check.data.through)}` : ""
        }.`}
        testId="storage-removed-archive-present"
      />
    );
  }

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Mark ${instance.name} storage removed`}
      description="Use this only when its stored data is already gone. Enabling it is then refused until it is set up again."
      size="medium"
      testId="instance-storage-removed-dialog"
      // Closes only when the call succeeds, so a refusal leaves the typing where it is.
      onBeforeClose={(reasonToClose) => reasonToClose !== "primaryAction"}
      primaryAction={{
        label: "Mark storage removed",
        onClick: () => {
          setAsked(true);
          if (trimmed === "") return;
          onConfirm(trimmed);
        },
        // The archive answer is shown before anyone can mark.
        disabled: check.isPending,
        loading: isSaving,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="That change was not saved" error={error} /> : null}

        {archiveMessage()}

        <SkyTextarea
          label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded on the audit event beside your name, and shown in the tenant's Audit trail."
          validationState={asked && trimmed === "" ? "error" : "none"}
          validationMessage={asked && trimmed === "" ? "Say why the storage is gone." : undefined}
          maxLength={500}
          rows={3}
          testId="instance-storage-removed-reason"
        />
      </div>
    </SkyDialog>
  );
}
